import * as React from 'react';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import { FormControl } from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { cn } from '@Utils/cn';

type ComboboxItem = {
  value: string;
  label: string;
};

interface ComboboxProps {
  items: ComboboxItem[];
  value?: string;
  onChange?: (value: string) => void;

  placeholder?: string;
  disabled?: boolean;

  /** Wrap trigger in <FormControl> for shadcn forms */
  inForm?: boolean;
  id?: string;

  /** Popover behavior */
  inModal?: boolean;
  tabIndex?: number;

  /** Show spinner in empty state */
  loading?: boolean;

  /**
   * If true, store "value|label" in the form value.
   * If false, store just "value".
   */
  useValueWithLabel?: boolean;
  triggerClassName?: string;
}

export function Combobox({
  items,
  value,
  onChange,
  placeholder,
  disabled,
  inForm = false,
  id,
  inModal,
  tabIndex,
  loading = false,
  useValueWithLabel = false,
  triggerClassName,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const commandListRef = React.useRef<HTMLDivElement | null>(null);

  // For values like "id|Label", extract the id
  const actualValue = React.useMemo(() => {
    if (!value) return undefined;
    if (!useValueWithLabel) return value;
    return value.split('|')[0] ?? value;
  }, [value, useValueWithLabel]);

  // What text should show in the button
  const displayLabel = React.useMemo(() => {
    if (!value) return undefined;

    // If we stored "value|Label", prefer that label
    if (useValueWithLabel && value.includes('|')) {
      const [, label] = value.split('|');
      if (label) return label;
    }

    const match = items.find(
      (item) => item.value === actualValue || item.value === value,
    );
    return match?.label;
  }, [value, useValueWithLabel, items, actualValue]);

  const handleSelect = (item: ComboboxItem) => {
    const newValue = useValueWithLabel
      ? `${item.value}|${item.label}`
      : item.value;
    onChange?.(newValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={inModal}>
      <PopoverTrigger asChild tabIndex={tabIndex ?? undefined}>
        {inForm ? (
          <FormControl id={id}>
            <Button
              variant="outline"
              role="combobox"
              className={cn(
                'w-full justify-between',
                !displayLabel && 'text-muted-foreground',
                'overflow-hidden text-ellipsis whitespace-nowrap',
                triggerClassName,
              )}
              disabled={disabled}
            >
              {displayLabel || placeholder || 'Select an item'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </FormControl>
        ) : (
          <Button
            variant="outline"
            role="combobox"
            className={cn(
              'w-full justify-between',
              !displayLabel && 'text-muted-foreground',
              'overflow-hidden text-ellipsis whitespace-nowrap',
              triggerClassName,
            )}
            disabled={disabled}
          >
            {displayLabel || placeholder || 'Select an item'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>

      <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput
            placeholder={placeholder ?? 'Search for an item...'}
            onValueChange={() => {
              if (commandListRef.current) {
                commandListRef.current.scrollTop = 0;
              }
            }}
          />
          <CommandList ref={commandListRef}>
            <CommandEmpty>
              {loading ? (
                <div className="flex w-full items-center justify-center p-4">
                  <span className="animate-spin">
                    <Loader2 className="h-4 w-4 text-gray-500" />
                  </span>
                </div>
              ) : (
                'No items found.'
              )}
            </CommandEmpty>
            <CommandGroup>
              {!loading &&
                items.map((item) => (
                  <CommandItem
                    key={item.value}
                    // Command uses `value` for filtering; we can shove label in there too
                    value={
                      item.value.includes('|')
                        ? item.value
                        : `${item.value}|${item.label}`
                    }
                    onSelect={() => handleSelect(item)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        item.value === actualValue
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    {item.label}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
